<template>
  <div>
    <global-confirm-box ref="confirmBox" />
    <warehouse-waybill-dialog
      v-model="waybillDialog"
      :edited-item="editedItem"
      @success="load"
    />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in documentsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="toLatin(group.warehouseName)"
          item-key="id"
          show-filters
          :fields-per-row="null"
          :show-row-number="false"
          :bold-fields="['number']"
        >
          <template v-slot:item.document_date="{ item }">
            {{ localeDate(item.document_date) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <circle-button
                @click="editItem(item)"
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
                class="mr-2"
              />
              <remove-button @click="deleteItem(item)" />
            </v-row>
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-receipt-text-arrow-right-outline"
        :title="$t('no_invoices')"
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_invoice') + ': ' + lastEntryDate
            : $t('no_invoices_yet')
        "
      >
        <warehouse-button-waybill @click="openWaybillDialog" />
      </empty-state>

      <div class="fixed-bottom-button">
        <v-btn color="blue" block @click="openWaybillDialog" dark>
          {{ $t("warehouse.release") }}
          <v-icon class="ml-2">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <!-- Aggregate View -->
      <template v-if="aggregateMode">
        <v-data-table
          v-if="activeAggregatedItems.length > 0 || loading"
          :headers="aggregateHeaders"
          :items="activeAggregatedItems"
          :loading="loading"
          class="striped-table ordera-box-shadow"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
                {{ $t("warehouse.last_invoice") }}:
                <strong>{{ lastEntryDate }}</strong>
              </span>
              <v-spacer />
              <v-btn-toggle v-model="aggregateMode" dense class="mr-3">
                <v-btn :value="true" small>
                  <v-icon left small>mdi-sigma</v-icon>
                  {{ $t("warehouse.aggregate") }}
                </v-btn>
              </v-btn-toggle>
              <warehouse-button-waybill @click="openWaybillDialog" />
            </v-toolbar>
          </template>
          <template v-slot:item.grocery_name="{ item }">
            {{ toLatin(item.grocery_name) || "-" }}
          </template>
          <template v-slot:item.total_quantity="{ item }">
            {{ formatNumber(item.total_quantity) }}
          </template>
        </v-data-table>
        <empty-state
          v-if="!loading && activeAggregatedItems.length === 0"
          icon="mdi-sigma"
          :title="$t('warehouse.no_aggregate_data')"
          :subtitle="$t('warehouse.no_entries_for_period')"
        />
      </template>

      <!-- Regular Document View -->
      <v-data-table
        v-else-if="displayedDocuments.length > 0 || loading"
        :headers="headers"
        :items="displayedDocuments"
        class="striped-table ordera-box-shadow"
        :loading="loading"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
              {{ $t("warehouse.last_invoice") }}:
              <strong>{{ lastEntryDate }}</strong>
            </span>
            <v-spacer />
            <v-btn-toggle v-model="aggregateMode" dense class="mr-3">
              <v-btn :value="true" small>
                <v-icon left small>mdi-sigma</v-icon>
                {{ $t("warehouse.aggregate") }}
              </v-btn>
            </v-btn-toggle>
            <warehouse-button-waybill @click="openWaybillDialog" />
          </v-toolbar>
        </template>
        <template v-slot:item.num="{ item, index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              @click="editItem(item)"
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              class="mr-2"
            />
            <remove-button @click="deleteItem(item)" />
          </v-row>
        </template>
        <template v-slot:item.warehouse.name="{ item }">
          {{
            toLatin(item.warehouse?.name) || "Nemamo dodat warehouse. Trebamo proveriti"
          }}
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
      </v-data-table>

      <empty-state
        v-if="!aggregateMode && !loading && displayedDocuments.length === 0"
        icon="mdi-receipt-text-arrow-right-outline"
        :title="$t('no_invoices')"
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_invoice') + ': ' + lastEntryDate
            : $t('no_invoices_yet')
        "
        @click:subtitle="setFiltersToLastEntry()"
      >
        <warehouse-button-waybill @click="openWaybillDialog" />
      </empty-state>
    </template>
  </div>
</template>

<script>
import WarehouseWaybillDialog from "@/components/warehouse/dialogs/WarehouseWaybillDialog.vue";
import { WarehouseButtonWaybill } from "@/components/warehouse/buttons";
import { waybillDocumentGrocery } from "@/models/warehouse";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import warehouseDocumentTabMixin from "@/mixins/warehouse/warehouseDocumentTabMixin";
import warehouseService from "@/services/warehouse";

export default {
  name: "WarehouseTabInvoice",
  mixins: [warehouseDocumentTabMixin],
  components: {
    WarehouseWaybillDialog,
    WarehouseButtonWaybill,
    EmptyState,
    MobileDataCards,
  },
  data: () => ({
    documentType: "exit",
    waybillDialog: false,
    editedItem: { ...waybillDocumentGrocery, groceries: [] },
  }),
  created() {
    this.load();
  },
  methods: {
    openWaybillDialog() {
      this.editedItem = { ...waybillDocumentGrocery, groceries: [], warehouse: this.filters.warehouse[0] };
      this.waybillDialog = true;
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.waybillDialog = true;
    },

    async deleteItem(item) {
      const confirmed = await this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_issue"),
          this.$t("warehouse.confirm_delete_issue"),
          {
            color: "warning",
          }
        )
        .catch(() => false);

      if (confirmed) {
        try {
          await warehouseService.removeWarehouse(item.id);
          this.documents = this.documents.filter((i) => i.id !== item.id);
          this.showSuccessSnackbar(this.$t("invoice_deleted"));
        } catch (err) {
          this.showErrorSnackbar(this.$t("error_deleting_invoice"));
          console.warn(err);
        }
      }
    },
  },
  computed: {
    headers() {
      return [
        { text: "ID", value: "id", mobile: false },
        { text: this.$t("stock"), value: "warehouse.name", mobile: false },
        { text: this.$t("warehouse.document_number"), value: "number", cols: 6 },
        { text: this.$t("warehouse.waybill_date"), value: "document_date", cols: 6 },
        { text: this.$t("note"), value: "notes", cols: 9 },
        { text: this.$t("status_created"), value: "created_at", mobile: false, cols: 9 },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 3,
          width: "100px",
          align: "right",
        },
      ];
    },
    exportTitle() {
      const parts = [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("issue"),
      ];
      if (this.aggregateMode) parts.push(this.$t("warehouse.aggregate"));
      return parts.join(" ");
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 5;
}
</style>
